import { type ReactElement } from "react";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { Text } from "~/components/text";
import { cn } from "~/utils";
import { RegionBadge } from "./region-badge";
import {
  formatSpanDateRange,
  LEAD_TIME_DAYS_THRESHOLD,
  type CalendarEventEntry,
} from "./calendar-utils";

interface CalendarEventRowProps {
  entry: CalendarEventEntry;
  onSelect: (entry: CalendarEventEntry) => void;
}

const leadTimeLabel = (
  entry: CalendarEventEntry,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  if (entry.isPast) {
    return t("components:seasonalEventModal.calendar.pastEvent");
  }
  if (entry.leadTimeDays < 0) {
    return t("components:seasonalEvents.leadTime.ongoing");
  }
  if (entry.leadTimeDays === 0) {
    return t("components:seasonalEvents.leadTime.today");
  }
  if (entry.leadTimeDays < LEAD_TIME_DAYS_THRESHOLD) {
    return t("components:seasonalEvents.leadTime.days", {
      count: entry.leadTimeDays,
    });
  }
  return t("components:seasonalEvents.leadTime.weeks", {
    count: entry.leadTimeWeeks,
  });
};

export const CalendarEventRow = ({
  entry,
  onSelect,
}: CalendarEventRowProps): ReactElement => {
  const { t } = useTranslation(["components"]);
  const chipDate = entry.isSpan
    ? parseISO(`${entry.monthKey}-01`)
    : parseISO(entry.occurrence.start);
  const content = (
    <>
      <span
        className={cn(
          "flex size-12 shrink-0 flex-col items-center justify-center rounded-lg",
          entry.isPast ? "bg-secondary-100/80" : "bg-secondary-100",
          entry.isSpan ? "px-1" : "gap-0",
        )}
      >
        {entry.isSpan ? (
          <Text as="span" variant={entry.isPast ? "detail" : "title"}>
            {format(chipDate, "MMM").toUpperCase()}
          </Text>
        ) : (
          <>
            <Text as="span" variant="overlineStrong">
              {format(chipDate, "MMM")}
            </Text>
            <Text as="span" variant={entry.isPast ? "headingMuted" : "heading"}>
              {format(chipDate, "d")}
            </Text>
          </>
        )}
      </span>

      <div className="min-w-0 flex-1">
        <Text
          as="span"
          variant={entry.isPast ? "detail" : "bodyStrong"}
          className="block truncate"
        >
          {entry.event.display_name}
        </Text>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <RegionBadge region={entry.event.region} />
          {entry.isSpan ? (
            <Text as="span" variant="detail">
              {formatSpanDateRange(
                entry.occurrence.start,
                entry.occurrence.end,
              )}
            </Text>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <div className="text-right">
          <Text
            as="span"
            variant={entry.isPast ? "detail" : "link"}
            className="block"
          >
            {leadTimeLabel(entry, t)}
          </Text>
          {!entry.isPast ? (
            <Text as="span" variant="muted" className="block">
              {t(
                "components:seasonalEventModal.calendar.suggestedSearchesCount",
                { count: entry.event.prompts.length },
              )}
            </Text>
          ) : null}
        </div>
        {!entry.isPast ? (
          <ChevronRight className="size-4 text-secondary-400" aria-hidden />
        ) : null}
      </div>
    </>
  );

  if (entry.isPast) {
    return (
      <div className="flex w-full items-center gap-3 rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-3">
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(entry)}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border border-border bg-background px-3 py-3 text-left",
        "transition-colors hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {content}
    </button>
  );
};
